import React from "react";
import { useQuery } from "@apollo/client";

import appInfo from "utility/app-info";
import { LEARNER_SCREEN_SCHEDULE } from "utility/graphQl/query";
import { useGroupSessionActions } from "../../hooks/use-group-session-actions";

import { SessionTile } from "components/ui";
import {
  PageContainer,
  PageHeader,
  PageContent,
  PageLoading,
  PageError,
} from "apps/user/learner/components/page-container";

import { useSearch, Search } from "components/ui/Search";
import { FilterTags } from "apps/user/common/components/filter-tags";

import { logError } from "utility/Function";
import { useTranslation } from "lib/i18n/use-translation";
import { LearnerSessionCardWebPartsFragment } from "../../api/types";

import calendarIcon from "Assets/icon/icon-calendar.svg";

import styles from "./index.module.scss";

// Wrapper component that provides actions to SessionTile
const SessionTileWithActions = ({
  session,
  listName,
  listPosition,
}: {
  session: LearnerSessionCardWebPartsFragment;
  listName: string;
  listPosition: number;
}) => {
  const actions = useGroupSessionActions({ session });

  return (
    <SessionTile
      session={session}
      listName={listName}
      listPosition={listPosition}
      actions={actions}
    />
  );
};

const Schedule = () => {
  const { t } = useTranslation(["sessions", "index"]);
  const { searchQuery, onSubmitSearchQuery } = useSearch();
  const [filterTagsSelected, setFilterTagsSelected] = React.useState([]);
  const { loading, error, data } = useQuery(LEARNER_SCREEN_SCHEDULE, {
    fetchPolicy: "cache-and-network",
    onError(error) {
      logError("Error while fetching learner schedule", error);
    },
    variables: {
      appInfo,
    },
  });

  if (loading && !data) {
    return (
      <PageContainer>
        <PageHeader transparent />
        <PageContent maxWidth="lg">
          <div className={styles["page-title"]}>
            <img src={calendarIcon} alt="calendar" />
            <h1 className={styles["heading"]}>
              {t("index:navTitles.schedule")}
            </h1>
          </div>
          <div className={styles["search-container"]}>
            <Search
              searchQuery={searchQuery}
              onSubmitSearchQuery={onSubmitSearchQuery}
              autoFocus={!data}
              searchOnType
            />
          </div>
          <div className={styles["filters-container"]}>
            <FilterTags
              filterTags={[]}
              onChange={(data) => setFilterTagsSelected(data)}
            />
          </div>
          <PageLoading />
        </PageContent>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader transparent />
        <PageContent maxWidth="lg">
          <div className={styles["page-title"]}>
            <img src={calendarIcon} alt="calendar" />
            <h1 className={styles["heading"]}>
              {t("index:navTitles.schedule")}
            </h1>
          </div>
          <div className={styles["search-container"]}>
            <Search
              searchQuery={searchQuery}
              onSubmitSearchQuery={onSubmitSearchQuery}
              searchOnType={false}
            />
          </div>
          <div className={styles["filters-container"]}>
            <FilterTags
              filterTags={[]}
              onChange={(data) => setFilterTagsSelected(data)}
            />
          </div>
          <PageError />
        </PageContent>
      </PageContainer>
    );
  }

  const { contentLists } = data.learner.screens.schedule;
  const contentList = contentLists[0];

  const filterTags = [...contentList.services, ...contentList.durations];
  const servicesSelected = filterTagsSelected.filter((tag) =>
    contentList.services.includes(tag)
  );
  const durationsSelected = filterTagsSelected.filter((tag) =>
    contentList.durations.includes(tag)
  );
  const filteredItems = contentList.items
    .filter((item) => {
      if (!filterTagsSelected.length) {
        return true;
      } else {
        return (
          (servicesSelected.length === 0 ||
            servicesSelected.some((service) => item.service === service)) &&
          (durationsSelected.length === 0 ||
            durationsSelected.some((duration) => item.duration === duration))
        );
      }
    })
    .filter((item) => {
      const sanitizedSearchQuery = searchQuery.replace(/\s+/g, " ").trim();
      const {
        practitioner: { fullName },
        title,
      } = item;

      return (
        fullName.toLowerCase().includes(sanitizedSearchQuery.toLowerCase()) ||
        title.toLowerCase().includes(sanitizedSearchQuery.toLowerCase())
      );
    });

  return (
    <PageContainer>
      <PageHeader transparent />
      <PageContent maxWidth="lg">
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div className={styles["page-title"]}>
            <img src={calendarIcon} alt="calendar" />
            <h1 className={styles["heading"]}>
              {t("index:navTitles.schedule")}
            </h1>
          </div>
          <div className={styles["search-container"]}>
            <Search
              searchQuery={searchQuery}
              onSubmitSearchQuery={onSubmitSearchQuery}
              autoFocus={!data}
            />
          </div>
          <div className={styles["filters-container"]}>
            <FilterTags
              filterTags={filterTags}
              onChange={(data) => setFilterTagsSelected(data)}
            />
          </div>
          {contentList.items.length && !filteredItems.length ? (
            <PageError>
              <div className={styles["no-results"]}>
                <h3>{t("sessions:schedule.noResults.title")}</h3>
                <p>{t("sessions:schedule.noResults.intro")}</p>
                <p>{t("sessions:schedule.noResults.tipSearch")}</p>
                <p>{t("sessions:schedule.noResults.tipFilters")}</p>
              </div>
            </PageError>
          ) : (
            filteredItems.map((item, index) => (
              <SessionTileWithActions
                key={item.id}
                session={item}
                listName={contentLists[0].id}
                listPosition={index + 1}
              />
            ))
          )}
        </div>
      </PageContent>
    </PageContainer>
  );
};

export default Schedule;
